import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  ArrowLeftRight,
  CircleCheckBig,
  FileText,
  Loader,
  Upload,
  DownloadCloud,
} from "lucide-react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { LinkedAccountsContext } from "../../../providers/LinkedAccountsProvider";
import { handleApiError, readExcelFile } from "../../../lib/utils";
import { bulkUploadTransactionsUpload } from "./BankingApis";
import { UIContext } from "../../../providers/UIProvider";
import * as XLSX from "xlsx";
import { Check } from "../../../assets";

interface UploadedFileData {
  Amount: number;
  TransactionName: string;
  TransactionDate: string;
}

const UploadFilesModal = ({
  isFileUploadModalOpen,
  handleChangeUploadModal,
}: {
  isFileUploadModalOpen: boolean;
  handleChangeUploadModal: (open: boolean) => void;
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [progress, setProgress] = useState(0);
  const [validData, setValidData] = useState<UploadedFileData[]>([]);
  const [errorRow, setErrorRow] = useState<null | {
    rowIndex: number;
    error: string;
  }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { bookEZAccounts, refetchAccounts } = useContext(LinkedAccountsContext);
  const [isDragOver, setIsDragOver] = useState(false);

  const fetchExcelData = async (file: File) => {
    setProgress(0);
    setErrorRow(null);
    setValidData([]);

    try {
      const { validRows, invalidRow } = await readExcelFile(file, (percent) => {
        setProgress(percent);
      });

      if (invalidRow) {
        setErrorRow({ rowIndex: invalidRow.rowIndex, error: invalidRow.error });
        setValidData([]);
      } else {
        setValidData(validRows);
      }
    } catch (err) {
      console.error("Error reading file:", err);
    }
  };

  useEffect(() => {
    if (uploadedFile) {
      fetchExcelData(uploadedFile);
    }
  }, [uploadedFile]);

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    if (!uploadedFile) {
      setIsDragOver(false);
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (
          file.type === "text/csv" ||
          file.name.endsWith(".csv") ||
          file.name.endsWith(".xls") ||
          file.name.endsWith(".xlsx")
        ) {
          setUploadedFile(file);
        }
      }
    }
  };

  const handleCardClick = () => {
    if (!uploadedFile) {
      fileInputRef.current?.click();
    }
  };

  const handleDeleteFile = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setUploadedFile(null);
    fileInputRef.current && (fileInputRef.current.value = "");
    setErrorRow(null);
    setValidData([]);
    setProgress(0);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSubmit = async () => {
    const result = await bulkUploadTransactionsUpload(
      selectedAccount,
      validData,
      businessId,
      onError,
      handleApiError
    );
    if (result.success) {
      onSuccess?.(
        "POST",
        "uploadTransactions",
        "Transactions uploaded successfully!"
      );
      refetchAccounts();
      handleCloseModal();
    }
  };

  // Function to download demo Excel file
  const downloadDemoExcel = () => {
    const demoData = [
      {
        Amount: 100.5,
        TransactionName: "Uber",
        TransactionDate: "2025-06-01T00:00:00",
      },
      {
        Amount: 200.75,
        TransactionName: "Starbucks",
        TransactionDate: "2025-06-02T00:00:00",
      },
      {
        Amount: 50.25,
        TransactionName: "Amazon",
        TransactionDate: "2025-06-03T00:00:00",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(demoData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Demo Data");

    // Generate the Excel file
    XLSX.writeFile(wb, "transaction_file.xlsx");
  };

  const handleCloseModal = () => {
    setUploadedFile(null);
    setSelectedAccount("");
    setProgress(0);
    setValidData([]);
    setErrorRow(null);
    setIsDragOver(false);
    handleChangeUploadModal(false);
  };

  return (
    <Dialog open={isFileUploadModalOpen} onOpenChange={handleCloseModal}>
      <DialogContent className="w-[90vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6">
        <DialogTitle className="text-sm sm:text-lg! flex items-center gap-2 border-b pb-2 border-gray-200 BookEZ_font_color_gray_2 font-bold">
          Select an account
        </DialogTitle>
        <div className="flex md:justify-between md:items-center flex-col md:flex-row w-full">
          <p className="text-lg BookEZ_font_color_gray_2 !font-medium py-5">
            Select a BookEZ account for the bank file you want to upload
          </p>
          <Button variant={"outline"} onClick={downloadDemoExcel}>
            <DownloadCloud className="w-4 h-4 mr-2" />
            Download demo file
          </Button>
        </div>

        <div className="w-full flex items-stretch gap-4 flex-col lg:flex-row">
          {/* Uploaded Bank File */}
          <Card
            className={`flex-1 border-2 transition-all duration-200 cursor-pointer p-4 relative ${
              isDragOver
                ? "border-blue-400 bg-blue-50"
                : "BookEZ_tab_bg_light border-gray-200"
            }`}
            onClick={handleCardClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold BookEZ_font_color_gray_2">
                Uploaded Bank File
              </h3>
              {uploadedFile && (
                <button
                  onClick={handleDeleteFile}
                  className="text-gray-500 hover:text-red-600 text-xl leading-none"
                >
                  ×
                </button>
              )}
            </div>

            {uploadedFile ? (
              <>
                <div className="flex items-center gap-5">
                  <div className="flex items-center gap-1">
                    <FileText />
                    <p className="font-medium BookEZ_font_color_gray_2">
                      {uploadedFile.name}
                    </p>
                    <p className="text-xs BookEZ_font_color_gray_2">
                      ({(uploadedFile.size / 1024).toFixed(1)} KB)
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <p
                      className={
                        !errorRow
                          ? "BookEZ_font_color_gray_2"
                          : "BookEZ_error_text"
                      }
                    >
                      {!errorRow
                        ? progress === 100
                          ? "Uploaded"
                          : `Uploading ${progress}%`
                        : null}
                    </p>
                    {!errorRow &&
                      (progress === 100 ? (
                        <CircleCheckBig color="green" />
                      ) : (
                        <Loader className="animate-spin" />
                      ))}
                  </div>
                </div>

                {errorRow && (
                  <p className="BookEZ_error_text !text-sm pt-1">
                    ❌ Error detected at row {errorRow.rowIndex}: Please ensure
                    the keys
                    <strong> Amount</strong>,<strong> TransactionName</strong>,
                    and
                    <strong> TransactionDate</strong> (<em>YYYY-MM-DD</em>) are
                    present and correctly named and formatted.
                  </p>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div className="text-sm BookEZ_font_color_gray_2 flex items-center gap-2">
                  <Upload />
                  <p className="font-medium BookEZ_font_color_gray_2">
                    {isDragOver
                      ? "Drop your file here"
                      : "Click to upload or drag and drop"}
                  </p>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={handleFileUpload}
              className="hidden"
            />
          </Card>

          {/* Arrow */}
          <div className="flex items-center justify-center px-2">
            {uploadedFile && !errorRow && selectedAccount ? (
              <Check />
            ) : (
              <ArrowLeftRight className="w-6 h-6 text-gray-600" />
            )}
          </div>

          {/* Book-ez Account */}
          <Card className="flex-1 BookEZ_tab_bg_light border-gray-200 p-4">
            <h3 className="text-lg font-semibold BookEZ_font_color_gray_2 mb-4">
              Book-ez Account
            </h3>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium BookEZ_font_color_gray_2">
                Select Account
              </label>
              <Select
                value={selectedAccount}
                onValueChange={setSelectedAccount}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder="Select Account"
                    className="BookEZ_font_color_gray_2"
                  />
                </SelectTrigger>
                <SelectContent>
                  {bookEZAccounts?.map((account, index) => (
                    <SelectItem key={index} value={account.accountId}>
                      {account.accountName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        </div>

        {/* Footer Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <Button
            variant="filled"
            disabled={!validData.length || !selectedAccount}
            className="h-[44px] text-md w-[110px]"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadFilesModal;
